import React from "react";
import { useUserContext } from "../../../../utils/contexts/UserProvider";
import { UserInfoItem } from "./ContactItem";
import { useTranslation } from "react-i18next";
import { DetailsCard } from "./DetailsCard";
import {
  Checkmark,
  Edit,
  Email,
  Identification,
  Location,
  Mobile,
  UserAvatarFilledAlt,
} from "@carbon/icons-react";
import { Button } from "../../../atom/Button";
import { EditAddressModal } from "./EditAddressModal";
import { EMPTY_VALUE, EMPTY_VALUE_PASEL_PASSPORT_RPU } from "../../../../utils/value";
import { ServerFile } from "../../../../types/components";
import { usePutResource } from "../../../../services";
import { getAgreementFile } from "../../../../services/apiHelpers";
import { DataAgreementModal } from "../../../atom/DataAgreementModal";
import { LabeledSeparator } from "../../../atom/LabeledSeparator";
import {
  getAgentParticipantsArrayWithoutAcceptedAgreement,
  getAgentParticipantWithoutAcceptedAgreement,
  hasNotAcceptedAgreements,
} from "../../../../utils/user";
import { ParticipantAgent } from "../../../../types/context";

export const UserProfile = () => {
  const { user, setUser } = useUserContext();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isAgreementModalOpen, setIsAgreementModalOpen] = React.useState(false);
  const [agreementFile, setAgreementFile] = React.useState<{
    id: string;
    file: ServerFile;
    text?: string;
    agentParticipant?: ParticipantAgent;
  } | null>(null);
  const { updateResource: updateUserResource } = usePutResource("/common/user");
  const { updateResource: updateAgentParticipantResource } = usePutResource(
    "/common/agent-participant",
  );

  const address_section = [
    {
      name: "Street (if applicable)",
      value: user?.street,
    },
    {
      name: "Block number",
      value: user?.block_number,
    },
    {
      name: "Apartment number (if applicable)",
      value: user?.apartment_number,
    },
    {
      name: "Postal code",
      value: user?.postal_code,
    },
    {
      name: "City",
      value: user?.city,
    },
  ];

  const paselPassportValue = user?.pesel || user?.passport_number;

  const handleConfirmData = async () => {
    const agentParitcipantsWithoutAgreement =
      getAgentParticipantsArrayWithoutAcceptedAgreement(user);

    if (agentParitcipantsWithoutAgreement?.length === 0 && user?.is_agreement_accepted) {
      return;
    }

    setUser((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        participant_agents: prev.participant_agents.map((participantAgent) => ({
          ...participantAgent,
          latest_accepted_agreement: agentParitcipantsWithoutAgreement.find(
            (ap) => ap.id === participantAgent.id,
          )
            ? null
            : participantAgent.latest_accepted_agreement,
        })),
      };
    });

    if (!user?.is_agreement_accepted) {
      await updateUserResource({
        resourceId: user?.id,
        is_confirming_data: true,
      });

      const [agreementFile]: [
        {
          id: string;
          file: ServerFile;
          text: string;
        } | null,
      ] = await getAgreementFile(user?.id);

      setAgreementFile(agreementFile);
      setIsAgreementModalOpen(true);

      return;
    }

    const agentParticipant = agentParitcipantsWithoutAgreement?.[0];

    if (!agentParticipant) {
      return;
    }

    const { data } = (await updateAgentParticipantResource({
      resourceId: agentParticipant.id,
      is_confirming_data: true,
      params: "?include=latest_agreement,latest_agreement.file",
    })) as { data: ParticipantAgent };

    setAgreementFile(
      data?.latest_agreement?.file
        ? { id: data?.id, file: data?.latest_agreement?.file, agentParticipant }
        : null,
    );
    setIsAgreementModalOpen(true);
  };

  const handleConfirmAgreement = async () => {
    if (!user) {
      return;
    }

    const localUser = user;

    if (!agreementFile?.agentParticipant) {
      await updateUserResource({
        resourceId: user?.id,
        is_agreement_accepted: true,
        is_accepting_agreement: true,
      });

      localUser.is_agreement_accepted = true;
      setUser(localUser);
    }

    if (agreementFile?.agentParticipant) {
      await updateAgentParticipantResource({
        resourceId: agreementFile?.agentParticipant?.id,
        is_accepting_agreement: true,
      });

      localUser.participant_agents = localUser.participant_agents.map((participantAgent) =>
        participantAgent.id === agreementFile?.agentParticipant?.id
          ? {
              ...participantAgent,
              latest_accepted_agreement: agreementFile.file,
            }
          : participantAgent,
      );
      setUser(localUser);
    }

    if (hasNotAcceptedAgreements(localUser)) {
      const agentParticipant = getAgentParticipantWithoutAcceptedAgreement(localUser);

      if (!agentParticipant) {
        setIsAgreementModalOpen(false);
        return;
      }

      const { data } = (await updateAgentParticipantResource({
        resourceId: agentParticipant.id,
        is_confirming_data: true,
        params: "?include=latest_agreement,latest_agreement.file",
      })) as { data: ParticipantAgent };

      setAgreementFile(
        data?.latest_agreement?.file
          ? { id: data?.id, file: data?.latest_agreement?.file, agentParticipant }
          : null,
      );
      setIsAgreementModalOpen(true);

      return;
    }

    setIsAgreementModalOpen(false);
  };

  const participantAgents = user?.participant_agents.filter(
    (participantAgent) =>
      participantAgent.participant_status.code === "synced" &&
      participantAgent.agent.registered_number !== "11263009/A",
  );

  return (
    <div className="flex flex-col justify-start flex-1 w-full gap-5">
      <div className="relative bg-gray-100 rounded h-[72px]">
        <div className="absolute -bottom-[48px] left-4 rounded-full w-[64px] h-[64px] bg-white flex items-center justify-center">
          <UserAvatarFilledAlt size={48} className="text-ominimo-400" />
        </div>
      </div>

      <h2 className="pt-[48px] px-4 text-[28px] leading-[34px] font-medium">
        {user?.first_name} {user?.last_name}
      </h2>

      {participantAgents && participantAgents.length > 0 && (
        <>
          <LabeledSeparator label={"Ominimo Accounts"} />

          <div className="px-4">
            <div className="flex flex-col gap-2">
              {participantAgents?.map((participantAgent) => (
                <div key={participantAgent.id}>
                  <p className="font-semibold text-[18px] truncate line-clamp-1">
                    {participantAgent?.agent?.name}
                  </p>
                  <p className="text-sm text-gray-375">{participantAgent?.participant_email}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      <LabeledSeparator label={"Personal Data"} />

      <div className="px-4">
        <div className="mt-4 space-y-[24px]">
          <UserInfoItem
            icon={<Email size={24} />}
            label={t("E-mail address")}
            value={user?.email || EMPTY_VALUE}
            bold
          />
          <UserInfoItem
            icon={<Mobile size={24} />}
            label={t("Phone number")}
            value={user?.phone_number || EMPTY_VALUE}
            bold
          />
          <UserInfoItem
            icon={<Identification size={24} />}
            label={t("ID card number")}
            value={user?.identification_number || EMPTY_VALUE}
            bold
          />
          <UserInfoItem
            label={t("PESEL number (or Passport number)")}
            value={paselPassportValue || EMPTY_VALUE_PASEL_PASSPORT_RPU}
            bold
          />
          <UserInfoItem
            label={t("RPU number")}
            value={user?.rpu_number || EMPTY_VALUE_PASEL_PASSPORT_RPU}
            bold
          />
        </div>
      </div>
      <div className="flex flex-col px-4 justify-start">
        <DetailsCard
          icon={<Location size={18} className="text-ominimo-400" />}
          title={t("Address")}
          fields={address_section}
          hideBottomBorder
        />
      </div>

      <div className="flex items-center gap-2 mx-4 mb-12 mt-5">
        <Button
          onClick={() => setIsModalOpen(true)}
          label={t("Edit Information")}
          rightIcon={<Edit size={24} />}
          type="tertiary"
          additionalClassName="flex-1"
        />
        {hasNotAcceptedAgreements(user) && (
          <Button
            label={t("Confirm data")}
            type="primary"
            additionalClassName="flex-1"
            onClick={handleConfirmData}
            rightIcon={<Checkmark size={24} />}
          />
        )}
      </div>

      <DataAgreementModal
        isAgreementModalOpen={isAgreementModalOpen}
        setIsAgreementModalOpen={setIsAgreementModalOpen}
        handleConfirmAgreement={handleConfirmAgreement}
        agreementFile={agreementFile?.file}
      />
      <EditAddressModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleConfirmData={handleConfirmData}
      />
    </div>
  );
};
